import React, { useEffect, useRef } from 'react';

interface SnowProps {
  backgroundColor?: string; // Background color of the canvas
  snowColor?: string; // Color of snowflakes
  count?: number; // Number of snowflakes
  size?: number; // Maximum size of snowflakes
  speed?: number; // Speed of falling snowflakes
}

const Snowfall: React.FC<SnowProps> = ({ backgroundColor = 'lightblue', snowColor = 'white', count = 100, size = 5, speed = 1 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width = window.innerWidth;
    const height = canvas.height = window.innerHeight;

    const createSnowflake = () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * size,
      speed: Math.random() * speed + 1,
    });

    const generateSnowflakes = () => {
      const snowflakes = [];
      for (let i = 0; i < count; i++) {
        snowflakes.push(createSnowflake());
      }
      return snowflakes;
    };

    const animate = (snowflakes: Snowflake[]) => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height); 

      snowflakes.forEach((flake) => {
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
        ctx.fillStyle = snowColor;
        ctx.fill();

        flake.y += flake.speed;

        if (flake.y > height) {
          flake.y = 0;
          flake.x = Math.random() * width;
        }
      });
    };

    const snowflakes = generateSnowflakes();
    const interval = setInterval(() => animate(snowflakes), 30); // Update every 30 milliseconds

    return () => {
      clearInterval(interval);
    };
  }, [count, size, speed, backgroundColor, snowColor]); 

  return (
    <canvas 
      ref={canvasRef} 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        pointerEvents: 'none', 
        width: '100vw', 
        height: '100vh', 
        zIndex: -1,
      }} 
    />
  );
};

interface Snowflake {
  x: number;
  y: number;
  radius: number;
  speed: number;
}

export default Snowfall;